<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import ProgressSpinner from 'primevue/progressspinner'
import GbButton from '@/components/GbButton.vue'
import GbDialog from '@/components/GbDialog.vue'
import RadioRow from '@/components/RadioRow.vue'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import WikiText from '@/components/wikitext/WikiText.vue'
import WikiTextEditor from '@/components/editor/WikiTextEditor.vue'
import PresentLevelsStandardView from '@/standards/PresentLevelsStandardView.vue'
import ResourceCard from '@/resources/ResourceCard.vue'
import { headingsForStandardSetCode } from '@/standards/standard_headings'
import { useUserStore } from '@/authentication/user.store'
import { getSkillTypes } from '@/presentlevels/api_v1.js'
import { usePresentLevelsWizardStore } from '@/presentlevels/no-ai/present_levels_wizard.store'
import { useSkillStatement } from '@/presentlevels/no-ai/useSkillStatement'
import { LOCKED_DESCRIPTION } from '@/components/locked_copy'
import type { Skill, StudentStatement } from '@/presentlevels/no-ai/skill_statement_types'

const props = withDefaults(
  defineProps<{
    subject: string
    skill: Skill
    skillType?: symbol
    studentName?: string
    studentGrade?: string
    studentStatements?: StudentStatement[]
    presentLevelUuid?: string
    isMultiSelect?: boolean
    isLocked?: boolean
    // Clark County School District's effect statements replace impacts.
    isEffectsTemplate?: boolean
  }>(),
  {
    skillType: () => getSkillTypes().SKILL,
    studentName: undefined,
    studentGrade: undefined,
    studentStatements: () => [],
    presentLevelUuid: undefined,
    isMultiSelect: false,
    isLocked: false,
    isEffectsTemplate: false
  }
)

const {
  cardState,
  isEditing,
  hasLevelChoices,
  levelOptions,
  levelColumns,
  levelLabelPosition,
  levelGroupLabel,
  preselectedLevels,
  functionalGrade,
  multiSelectedStatements,
  selectedStatement,
  defaultText,
  wikiTextFeedback,
  statementsLoadError,
  populateDefaultStatement,
  viewStatements,
  editSaved,
  closeEdit,
  removeSaved,
  stopEditing,
  stopEditingMultiSelect,
  clearWikiTextFeedback,
  selectLevels
} = useSkillStatement(props)

const { blurPermissions } = storeToRefs(usePresentLevelsWizardStore())
const { userContentProfileId } = storeToRefs(useUserStore())

const isRemoveConfirmationShown = ref(false)
const isRemoving = ref(false)

const skillName = computed(() => props.skill.skill_name)
const hasStandards = computed(() => Object.keys(selectedStatement.value.standardsBySetCode).length > 0)
const hasResources = computed(() => Object.keys(selectedStatement.value.resources).length > 0)
const isLevelHeadingShown = computed(() => !props.isEffectsTemplate || multiSelectedStatements.value.length > 1)
const isSavedTextBlurred = computed(() => blurPermissions.value.blur)

// The active card's blue background drops gray-700 text (RadioRow's disabled levels, resource
// details) and blue-400 text (the resource Preview label) below 4.5:1, so they are darkened
// while the card is active (WCAG 1.4.3, #4152).
const cardClass = computed(() =>
  isEditing.value ? 'bg-objective-blueLight [&_.text-gray-700]:text-gray-900 [&_.text-blue-400]:text-objective-blue' : 'bg-white'
)

const standardHeading = (setCode: string) => headingsForStandardSetCode(setCode, { profileId: userContentProfileId.value })
const levelOf = (statement: StudentStatement) => statement.selected_statement.level_label
const statementUuidOf = (statement: StudentStatement) => statement.uuid ?? ''

const clearFeedbackWhenEditingStarts = (isEditMode: boolean) => {
  if (isEditMode) clearWikiTextFeedback()
}

const confirmRemove = async () => {
  isRemoveConfirmationShown.value = false
  if (isRemoving.value) return
  isRemoving.value = true
  try {
    await removeSaved()
  } finally {
    isRemoving.value = false
  }
}
</script>

<template>
  <div class="mb-8 rounded-xxs py-3 font-default shadow-lib-card" :class="cardClass" data-testid="skill-statement-card">
    <section class="flex items-center justify-between px-4 py-3">
      <h3 class="text-display-sm"><strong>{{ skillName }}</strong></h3>
      <Transition
        mode="out-in"
        enter-active-class="transition-opacity duration-300"
        leave-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        leave-to-class="opacity-0"
      >
        <span
          v-if="isLocked"
          key="locked"
          class="inline-flex cursor-not-allowed items-center rounded-xs bg-red-200 px-1 text-white"
          data-testid="locked-statement"
        >
          <span class="material-icons-round" aria-hidden="true">lock</span>
          <span class="sr-only">Add {{ skillName }} statement ({{ LOCKED_DESCRIPTION }})</span>
        </span>
        <span v-else-if="cardState === 'loading'" key="loading" class="flex items-center text-gray-900" role="status">
          <ProgressSpinner stroke-width="8" class="mr-1 h-4 w-4" />
          Adding...
        </span>
        <GbButton
          v-else-if="cardState === 'notIncluded'"
          key="add"
          gb-theme="greenPrimary"
          size="small"
          :aria-label="`Add ${skillName} statement`"
          @click="viewStatements"
        >
          Add
        </GbButton>
        <div v-else-if="cardState === 'saved'" key="saved" class="flex items-center gap-1">
          <GbButton
            gb-theme="bluePrimary"
            size="small"
            :aria-label="`Edit ${skillName} statement`"
            :disabled="isRemoving"
            @click="editSaved"
          >
            Edit
          </GbButton>
          <GbButton
            gb-theme="redTertiary"
            size="small"
            :aria-label="`Remove ${skillName} statement`"
            :disabled="isRemoving"
            @click="isRemoveConfirmationShown = true"
          >
            Remove
          </GbButton>
        </div>
        <GbButton v-else key="close" gb-theme="grayOutlineSecondary" size="small" :aria-label="`Close ${skillName}`" @click="closeEdit">
          Close
        </GbButton>
      </Transition>
    </section>
    <p v-if="statementsLoadError" role="alert" class="px-4 text-text-md text-red-300" data-testid="statements-load-error">
      {{ statementsLoadError }}
    </p>

    <template v-if="isEditing">
      <section class="px-4 py-3">
        <div v-if="hasLevelChoices" class="w-full py-2">
          <RadioRow
            :columns="levelColumns"
            :label-position="levelLabelPosition"
            :option-set="levelOptions"
            :default-options="preselectedLevels"
            :multi-select="isMultiSelect"
            :id-prefix="skill.skill_uuid"
            :group-label="levelGroupLabel"
            @change="selectLevels"
          />
        </div>

        <div class="py-2 text-text-2xl font-[300] leading-8">
          <CopyTrackWrapper v-if="!isMultiSelect" type="presentlevel" :title="skillName" :uuid="skill.skill_uuid" :level="functionalGrade" gate-copy>
            <WikiTextEditor
              format="markdown"
              :wiki-text="selectedStatement.text"
              :reset-value="defaultText"
              show-reset
              :resources="selectedStatement.resources"
              :label="`${skillName} statement`"
              editor-wrapper-class="bg-white p-2"
              @update:wiki-text="stopEditing"
              @edit-mode-updated="clearFeedbackWhenEditingStarts"
            >
              <template #bottomLeftContent>
                <div class="pl-1 text-md leading-5 text-black" aria-live="polite">{{ wikiTextFeedback }}</div>
              </template>
            </WikiTextEditor>
          </CopyTrackWrapper>
          <template v-else>
            <CopyTrackWrapper
              v-for="statement in multiSelectedStatements"
              :key="statement.uuid"
              class="mb-4 last:mb-0"
              type="presentlevel"
              :title="skillName"
              :uuid="skill.skill_uuid"
              :level="levelOf(statement)"
              gate-copy
            >
              <h4 v-if="isLevelHeadingShown" class="py-2 text-xl font-bold">{{ levelOf(statement) }}</h4>
              <WikiTextEditor
                format="markdown"
                :wiki-text="statement.statement_text"
                :reset-value="populateDefaultStatement(statement.selected_statement.statement_text)"
                show-reset
                :label="`${skillName}: ${levelOf(statement)} statement`"
                editor-wrapper-class="bg-white p-2"
                @update:wiki-text="(text: string) => stopEditingMultiSelect(text, statementUuidOf(statement), levelOf(statement))"
                @edit-mode-updated="clearFeedbackWhenEditingStarts"
              >
                <template #bottomLeftContent>
                  <div class="pl-1 text-md leading-5 text-black" aria-live="polite">{{ wikiTextFeedback }}</div>
                </template>
              </WikiTextEditor>
            </CopyTrackWrapper>
          </template>
        </div>
      </section>

      <section v-if="hasStandards" class="px-4 py-3" data-testid="statement-standards">
        <div v-for="(standards, setCode) in selectedStatement.standardsBySetCode" :key="setCode">
          <h4 class="py-2 text-xl font-bold text-blue-500">{{ standardHeading(setCode) }}</h4>
          <CopyTrackWrapper
            v-for="standard in standards"
            :key="standard.standard_long_ref"
            class="py-2 text-gray-900"
            type="pl-standard"
            :title="standard.standard_long_ref"
            :uuid="standard.standard_long_ref"
            gate-copy
          >
            <PresentLevelsStandardView
              :set-code="standard.standard_set_code"
              :short-ref="standard.standard_short_ref"
              :standard-text="standard.standard_text"
              :standard-info="standard"
            />
          </CopyTrackWrapper>
        </div>
      </section>

      <section v-if="hasResources" class="px-4 py-3" data-testid="statement-resources">
        <h4 class="py-2 text-xl font-bold text-blue-500">ASSESSMENT RESOURCES</h4>
        <ul class="list-none p-0">
          <li v-for="resource in selectedStatement.resources" :key="resource.uuid">
            <ResourceCard
              :uuid="resource.uuid"
              :title="resource.title"
              :num_pages="resource.num_pages"
              :description="resource.description"
              :type="resource.type"
              :screenshots="resource.screenshots"
              :thumbnail_url="resource.thumbnail_url"
              :grades="resource.grades"
              :subjects="resource.subjects"
              :download_links="resource.download_links"
              :google_drive_links="resource.google_drive_links"
              :heading-level="5"
            />
          </li>
        </ul>
      </section>
    </template>

    <section v-if="cardState === 'saved'" class="px-4 py-3" data-testid="saved-statement">
      <CopyTrackWrapper class="text-xl font-regular italic leading-7 text-[#275D8B]" type="presentlevel" :title="skillName" :uuid="skill.skill_uuid" gate-copy>
        <WikiText
          v-if="!isMultiSelect"
          class="fs-exclude"
          :text="selectedStatement.text"
          :resources="selectedStatement.resources"
          :locked="isSavedTextBlurred"
          :plaintext="isSavedTextBlurred"
          lock-mode="always_snippet_first_block"
        />
        <template v-else>
          <WikiText
            v-for="statement in multiSelectedStatements"
            :key="statement.uuid"
            class="fs-exclude"
            :text="statement.statement_text"
            :locked="isSavedTextBlurred"
            :plaintext="isSavedTextBlurred"
            lock-mode="always_snippet_first_block"
          />
        </template>
      </CopyTrackWrapper>
    </section>

    <GbDialog
      v-model:visible="isRemoveConfirmationShown"
      :dialog-id="`remove-statement-${skill.skill_uuid}`"
      header="Are you sure you want to remove this saved statement?"
    >
      <template #bodyContent>
        <div class="flex justify-center gap-1">
          <GbButton gb-theme="redPrimary" size="large" @click="confirmRemove">Remove</GbButton>
          <GbButton gb-theme="grayOutlineSecondary" size="large" @click="isRemoveConfirmationShown = false">No</GbButton>
        </div>
      </template>
    </GbDialog>
  </div>
</template>
